import { useState, type FormEvent } from 'react'
import { Input } from '@/components/ui/Input'
import { PasswordInput } from '@/components/ui/PasswordInput'
import { Button } from '@/components/ui/Button'
import { Checkbox } from '@/components/ui/Checkbox'
import { Text } from '@/components/ui/Text'
import { PasswordPolicyHint } from './PasswordPolicyHint'
import { passwordMeetsPolicy } from './password-policy'
import type { RegisterPolicy } from '@/lib/auth/register-api'
import type { Step1Data } from './signup-types'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

interface SignupStep1Props {
  policy: RegisterPolicy
  onNext: (data: Step1Data) => void
  /** Restore previously entered values when the user navigates back to this step. */
  initialValues?: Partial<Step1Data>
}

interface Step1Errors {
  pocName?: string
  email?: string
  password?: string
  confirmPassword?: string
  acceptedTerms?: string
}

export function SignupStep1({ policy, onNext, initialValues }: SignupStep1Props) {
  const [pocName, setPocName] = useState(initialValues?.pocName ?? '')
  const [email, setEmail] = useState(initialValues?.email ?? '')
  const [password, setPassword] = useState(initialValues?.password ?? '')
  const [confirmPassword, setConfirmPassword] = useState(initialValues?.confirmPassword ?? '')
  const [acceptedTerms, setAcceptedTerms] = useState(initialValues?.acceptedTerms ?? false)
  const [passwordTouched, setPasswordTouched] = useState(false)
  const [errors, setErrors] = useState<Step1Errors>({})
  const [submitted, setSubmitted] = useState(false)

  // Accepts overrides so onChange can pass the new value before React flushes the state update,
  // avoiding the stale-closure bug where validate() reads the old value from state.
  function validate(overrides?: {
    pocName?: string
    email?: string
    password?: string
    confirmPassword?: string
    acceptedTerms?: boolean
  }): Step1Errors {
    const n = overrides?.pocName ?? pocName
    const em = overrides?.email ?? email
    const pw = overrides?.password ?? password
    const cpw = overrides?.confirmPassword ?? confirmPassword
    const terms = overrides?.acceptedTerms ?? acceptedTerms
    const e: Step1Errors = {}
    if (!n.trim()) e.pocName = 'Your name is required'
    if (!EMAIL_REGEX.test(em)) e.email = 'Enter a valid email address'
    if (
      !passwordMeetsPolicy(pw, {
        minLength: policy.passwordPolicy.minLength,
        requireUppercase: policy.passwordPolicy.requireUppercase,
        requireLowercase: policy.passwordPolicy.requireLowercase,
        requireDigit: policy.passwordPolicy.requireDigit,
        requireSpecial: policy.passwordPolicy.requireSpecial,
      })
    )
      e.password = 'Password does not meet the requirements shown above'
    if (!cpw) e.confirmPassword = 'Please confirm your password'
    else if (pw !== cpw) e.confirmPassword = 'Passwords do not match'
    if (!terms) e.acceptedTerms = 'You must accept the terms to continue'
    return e
  }

  // Clear individual field errors as the user corrects them, but only after the first submit.
  function revalidate(overrides: Parameters<typeof validate>[0]) {
    if (submitted) setErrors(validate(overrides))
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setSubmitted(true)
    const errs = validate()
    setErrors(errs)
    if (Object.keys(errs).length > 0) return
    onNext({ pocName, email, password, confirmPassword, acceptedTerms })
  }

  function fieldError(key: keyof Step1Errors) {
    return submitted ? errors[key] : undefined
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="flex flex-col gap-4">
        {/* Full name */}
        <div>
          <label htmlFor="s1-pocName">
            <Text variant="label-sm" className="mb-1 block">
              Your full name
            </Text>
          </label>
          <Input
            id="s1-pocName"
            data-testid="step1-poc-name"
            value={pocName}
            onChange={(e) => { const v = e.target.value; setPocName(v); revalidate({ pocName: v }) }}
            placeholder="Jane Smith"
            autoComplete="name"
            aria-describedby={fieldError('pocName') ? 's1-pocName-err' : undefined}
            aria-invalid={!!fieldError('pocName')}
          />
          {fieldError('pocName') && (
            <Text id="s1-pocName-err" variant="body-sm" color="danger" className="mt-1">
              {errors.pocName}
            </Text>
          )}
        </div>

        {/* Work email */}
        <div>
          <label htmlFor="s1-email">
            <Text variant="label-sm" className="mb-1 block">
              Work email
            </Text>
          </label>
          <Input
            id="s1-email"
            data-testid="step1-email"
            type="email"
            value={email}
            onChange={(e) => { const v = e.target.value; setEmail(v); revalidate({ email: v }) }}
            placeholder="jane@company.com"
            autoComplete="email"
            aria-describedby={fieldError('email') ? 's1-email-err' : undefined}
            aria-invalid={!!fieldError('email')}
          />
          {fieldError('email') && (
            <Text id="s1-email-err" variant="body-sm" color="danger" className="mt-1">
              {errors.email}
            </Text>
          )}
        </div>

        {/* Password */}
        <div>
          <label htmlFor="s1-password">
            <Text variant="label-sm" className="mb-1 block">
              Password
            </Text>
          </label>
          <PasswordInput
            id="s1-password"
            data-testid="step1-password"
            value={password}
            onChange={(e) => { const v = e.target.value; setPassword(v); revalidate({ password: v }) }}
            onBlur={() => setPasswordTouched(true)}
            placeholder="Create a strong password"
            autoComplete="new-password"
            aria-describedby="s1-pw-rules"
            aria-invalid={!!fieldError('password')}
          />
          <div id="s1-pw-rules">
            <PasswordPolicyHint
              policy={policy.passwordPolicy}
              value={password}
              touched={passwordTouched}
            />
          </div>
          {fieldError('password') && (
            <Text variant="body-sm" color="danger" className="mt-1">
              {errors.password}
            </Text>
          )}
        </div>

        {/* Confirm password */}
        <div>
          <label htmlFor="s1-confirm">
            <Text variant="label-sm" className="mb-1 block">
              Confirm password
            </Text>
          </label>
          <PasswordInput
            id="s1-confirm"
            data-testid="step1-confirm-password"
            value={confirmPassword}
            onChange={(e) => { const v = e.target.value; setConfirmPassword(v); revalidate({ confirmPassword: v }) }}
            placeholder="Re-enter your password"
            autoComplete="new-password"
            aria-describedby={fieldError('confirmPassword') ? 's1-confirm-err' : undefined}
            aria-invalid={!!fieldError('confirmPassword')}
          />
          {fieldError('confirmPassword') && (
            <Text id="s1-confirm-err" variant="body-sm" color="danger" className="mt-1">
              {errors.confirmPassword}
            </Text>
          )}
        </div>

        {/* Terms */}
        <div className="flex items-start gap-2">
          <Checkbox
            id="s1-terms"
            data-testid="step1-terms"
            checked={acceptedTerms}
            onCheckedChange={(v) => { const checked = v === true; setAcceptedTerms(checked); revalidate({ acceptedTerms: checked }) }}
            aria-describedby={fieldError('acceptedTerms') ? 's1-terms-err' : undefined}
            aria-invalid={!!fieldError('acceptedTerms')}
          />
          <label htmlFor="s1-terms" className="cursor-pointer">
            <Text variant="body-sm" color="muted">
              I agree to the{' '}
              <a
                href={policy.termsUrl ?? '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline underline-offset-2"
              >
                Terms of Service
              </a>
            </Text>
            {fieldError('acceptedTerms') && (
              <Text id="s1-terms-err" variant="body-sm" color="danger" className="mt-0.5 block">
                {errors.acceptedTerms}
              </Text>
            )}
          </label>
        </div>

        <Button data-testid="step1-next-btn" type="submit" className="w-full mt-2">
          Continue
        </Button>
      </div>
    </form>
  )
}
